import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, type ProductRow } from '@/lib/api'
import { fmtDate } from '@/lib/labels'

type PoLine = {
  id: string
  orderedQty: number
  receivedQty: number
  variantId?: string | null
  product: { id: string; sku: string; name: string }
  variant?: { id: string; sku: string; label: string; color: string } | null
}

type PurchaseOrder = {
  id: string
  reference: string
  supplier: string
  city: string
  status: string
  expectedAt: string | null
  lines: PoLine[]
}

const PO_TONE: Record<string, string> = {
  EXPECTED: 'warn',
  PARTIAL: 'warn',
  RECEIVED: 'ok',
  CANCELLED: 'bad',
  DRAFT: 'neutral',
}

export function Receptions() {
  const toast = useToast()
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [receive, setReceive] = useState<PurchaseOrder | null>(null)
  const [supplier, setSupplier] = useState('')
  const [city, setCity] = useState('Kinshasa')
  const [productId, setProductId] = useState('')
  const [qty, setQty] = useState('10')
  const [recvQty, setRecvQty] = useState<Record<string, string>>({})
  const [recvVariant, setRecvVariant] = useState<Record<string, string>>({})
  const [variantId, setVariantId] = useState('')

  const list = useQuery({
    queryKey: ['admin-purchase-orders'],
    queryFn: () => api<{ items: PurchaseOrder[] }>('/admin/purchase-orders?limit=50'),
  })
  const products = useQuery({
    queryKey: ['admin-products-lite'],
    queryFn: () =>
      api<{ items: ProductRow[] }>('/admin/products?limit=100'),
  })
  const productDetail = useQuery({
    queryKey: ['admin-product-for-po', productId],
    queryFn: () => api<{ variants: Array<{ id: string; sku: string; label?: string; color: string }> }>(
      `/admin/products/${productId}`,
    ),
    enabled: Boolean(productId),
  })
  const receiveDetail = useQuery({
    queryKey: ['admin-product-recv', receive?.lines.map((l) => l.product.id).join(',')],
    queryFn: async () => {
      const map: Record<string, Array<{ id: string; sku: string; label?: string; color: string }>> = {}
      for (const line of receive?.lines ?? []) {
        if (map[line.product.id]) continue
        const p = await api<{
          variants: Array<{ id: string; sku: string; label?: string; color: string }>
        }>(`/admin/products/${line.product.id}`)
        map[line.product.id] = p.variants ?? []
      }
      return map
    },
    enabled: Boolean(receive),
  })

  const create = useMutation({
    mutationFn: () =>
      apiMutate('/admin/purchase-orders', 'POST', {
        supplier,
        city,
        lines: [
          {
            productId,
            orderedQty: Number.parseInt(qty, 10) || 1,
            ...(variantId ? { variantId } : {}),
          },
        ],
      }),
    onSuccess: () => {
      toast.ok('Bon de commande créé')
      setCreateOpen(false)
      setSupplier('')
      setProductId('')
      setVariantId('')
      void qc.invalidateQueries({ queryKey: ['admin-purchase-orders'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const receiveMut = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/purchase-orders/${receive!.id}/receive`, 'POST', {
        lines: receive!.lines.map((l) => ({
          lineId: l.id,
          receivedQty: Number.parseInt(recvQty[l.id] ?? '0', 10) || 0,
          ...(recvVariant[l.id] ? { variantId: recvVariant[l.id] } : {}),
        })),
      }),
    onSuccess: () => {
      toast.ok('Réception enregistrée')
      setReceive(null)
      void qc.invalidateQueries({ queryKey: ['admin-purchase-orders'] })
      void qc.invalidateQueries({ queryKey: ['admin-inventory'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-end">
        <BtnPrimary onClick={() => setCreateOpen(true)}>Nouveau BO</BtnPrimary>
      </div>

      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="Aucune réception"
          body="Créez un bon de commande pour recevoir du stock."
          action={<BtnPrimary onClick={() => setCreateOpen(true)}>Nouveau BO</BtnPrimary>}
        />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Réf.', 'Fournisseur', 'Ville', 'Statut', 'Attendu', 'Lignes', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((po) => (
                <tr key={po.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 font-medium">{po.reference}</td>
                  <td className="px-4 py-2.5">{po.supplier}</td>
                  <td className="px-4 py-2.5">{po.city}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill label={po.status} tone={PO_TONE[po.status] ?? 'neutral'} />
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">{fmtDate(po.expectedAt)}</td>
                  <td className="px-4 py-2.5 text-sm text-second-admin">
                    {po.lines
                      .map(
                        (l) =>
                          `${l.product.sku}${l.variant ? `/${l.variant.label || l.variant.sku}` : ''} ${l.receivedQty}/${l.orderedQty}`,
                      )
                      .join(', ')}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {po.status !== 'RECEIVED' && po.status !== 'CANCELLED' ? (
                      <RowActions
                        items={[
                          {
                            label: 'Recevoir',
                            onSelect: () => {
                              setReceive(po)
                              const init: Record<string, string> = {}
                              const vars: Record<string, string> = {}
                              for (const l of po.lines) {
                                init[l.id] = String(Math.max(0, l.orderedQty - l.receivedQty))
                                if (l.variantId || l.variant?.id) {
                                  vars[l.id] = l.variantId ?? l.variant!.id
                                }
                              }
                              setRecvQty(init)
                              setRecvVariant(vars)
                            },
                          },
                        ]}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={createOpen}
        title="Nouveau bon de commande"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setCreateOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary
              disabled={!supplier || !productId || create.isPending}
              onClick={() => create.mutate()}
            >
              Créer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Fournisseur">
            <TextInput value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </Field>
          <Field label="Ville">
            <TextInput value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>
          <Field label="Produit">
            <select
              className="rounded-champ border border-bordure-admin bg-page px-3 py-2"
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value)
                setVariantId('')
              }}
            >
              <option value="">Choisir…</option>
              {(products.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.sku} · {p.name}
                </option>
              ))}
            </select>
          </Field>
          {(productDetail.data?.variants?.length ?? 0) > 0 && (
            <Field label="Variante">
              <select
                className="rounded-champ border border-bordure-admin bg-page px-3 py-2"
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
              >
                <option value="">Défaut</option>
                {productDetail.data!.variants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label ?? v.color} · {v.sku}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Quantité commandée">
            <TextInput type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={Boolean(receive)}
        title={receive ? `Recevoir ${receive.reference}` : 'Réception'}
        onClose={() => setReceive(null)}
        size="lg"
        footer={
          <>
            <BtnGhost onClick={() => setReceive(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={receiveMut.isPending} onClick={() => receiveMut.mutate()}>
              Valider réception
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {receive?.lines.map((l) => {
            const variants = receiveDetail.data?.[l.product.id] ?? []
            return (
              <div key={l.id} className="flex flex-col gap-2 rounded-champ border border-bordure-admin p-3">
                <Field
                  label={`${l.product.sku} · ${l.product.name} (déjà ${l.receivedQty}/${l.orderedQty})`}
                >
                  <TextInput
                    type="number"
                    min="0"
                    value={recvQty[l.id] ?? '0'}
                    onChange={(e) => setRecvQty((m) => ({ ...m, [l.id]: e.target.value }))}
                  />
                </Field>
                {variants.length > 0 && (
                  <Field label="Variante destinataire">
                    <select
                      className="rounded-champ border border-bordure-admin bg-page px-3 py-2"
                      value={recvVariant[l.id] ?? l.variantId ?? ''}
                      onChange={(e) =>
                        setRecvVariant((m) => ({ ...m, [l.id]: e.target.value }))
                      }
                    >
                      <option value="">Défaut</option>
                      {variants.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label ?? v.color} · {v.sku}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            )
          })}
        </div>
      </Modal>
    </div>
  )
}
